/*
 * ExternalConnectionForm.tsx : Un formulaire pour ajouter ou modifier une connexion externe.
 */

// Import React
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import FontAwesome
import {
	faDatabase,
	faGlobe,
	faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';

// Import Communs et Layout
import {
	finishFormSave,
	FormSaveActions,
	InputField,
	SecondaryButton,
	SelectField,
	useFormDirty,
} from '../../../common';
import CardHeader from '../../../layout/CardHeader';
import CardSection from '../../../layout/CardSection';

// Import ExternalConnection
import { ExternalConnectionData } from '../types/types';
import { externalConnectionsApi } from '../services/externalConnectionsApi';

// Composant ExternalConnectionForm
const ExternalConnectionForm: React.FC = () => {
	// Hooks de State
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);
	const [loadingTest, setLoadingTest] = React.useState(false);
	const userId = localStorage.getItem('userId');
	const userName = localStorage.getItem('name');
	const userFirstName = localStorage.getItem('firstName');
	const [formData, setFormData] = React.useState<ExternalConnectionData>({
		id: 0,
		name: '',
		driver: '',
		host: '',
		port: '',
		db: '',
		username: '',
		password: '',
		lastSync: undefined,
		createdBy: userId
			? {
					id: Number(userId),
					name: userName || '',
					firstName: userFirstName || '',
				}
			: undefined,
		createdAt: new Date()
			.toISOString()
			.replace('Z', '+00:00') as unknown as Date,
	});

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { isDirty, markClean } = useFormDirty(formData, baselineReady);

	// Une fois le composant monté et si un id est présent, fetch des données et préremplissage du formulaire
	React.useEffect(() => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		if (id) {
			externalConnectionsApi
				.get(Number(id), token)
				.then((data) => {
					setFormData(data);
				})
				.catch((error) => console.error(error))
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// Si pas d'id, on arrête le loading directement
			setLoading(false);
			setBaselineReady(true);
		}
	}, [id]);

	// Gestion de la soumission du formulaire
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const handleSave = (shouldLeave: boolean) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		const onLeave = () => navigate('/app/settings/infrastructure');
		// Si un id est présent, mise à jour de l'élément existant
		if (id) {
			// on supprime createdBy, createdAt et lastSync pour éviter les conflits lors de la mise à jour
			const keysToRemove = ['createdBy', 'createdAt', 'lastSync'] as const;

			const dataToUpdate = Object.fromEntries(
				Object.entries(formData).filter(
					([key]) =>
						!keysToRemove.includes(key as (typeof keysToRemove)[number])
				)
			);

			externalConnectionsApi
				.update(Number(id), dataToUpdate, token)
				.then(() => finishFormSave(shouldLeave, { onLeave, markClean }))
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		} else {
			// Sinon, création d'un nouvel élément
			externalConnectionsApi
				.create(formData, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave,
						markClean,
						stayOnEdit:
							!id && created?.id
								? () =>
										navigate(
											`/app/settings/externalConnections/${created.id}/edit`,
											{ replace: true }
										)
								: undefined,
					})
				)
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		// Empêcher le comportement par défaut du formulaire
		e.preventDefault();
		handleSave(true);
	};

	// Handle Formulaire : Changements des inputs et textareas
	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	// Gestion du test de connexion
	const handleTestConnection = () => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token || !formData.id) return;

		setLoadingTest(true);
		externalConnectionsApi
			.testConnection(formData.id, token)
			.then(() => {
				alert('Connexion réussie !');
			})
			.catch((error) => {
				console.error(error);
				alert('Échec de la connexion : ' + error.message);
			})
			.finally(() => setLoadingTest(false));
	};

	const handleTestPreview = () => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token || !formData.id) return;

		setLoadingTest(true);
		externalConnectionsApi
			.previewMovements(formData.id, token)
			.then(() => {
				alert('Accès à la table principale réussi !');
			})
			.catch((error) => {
				console.error(error);
				alert("Échec de l'accès à la table principale : " + error.message);
			})
			.finally(() => setLoadingTest(false));
	};

	// Rendu du composant, le premier div est la card principale
	return (
		<div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-md space-y-6">
			{/* HEADER DE LA CARD */}
			<CardHeader
				icon={faGlobe}
				title={
					id
						? "Édition d'une connection externe"
						: 'Nouvelle connection externe'
				}
				createdAt={
					formData.createdAt ? formData.createdAt?.toLocaleString() : undefined
				}
				createdBy={
					formData.createdBy
						? formData.createdBy.firstName + ' ' + formData.createdBy.name
						: undefined
				}
				loading={loading}
			/>

			{/* CONTENU DE LA CARD */}
			<form onSubmit={handleSubmit} className="space-y-6">
				{/* 2 COLONNES */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					{/* COLONNE GAUCHE */}
					<CardSection icon={faInfoCircle} title="Informations générales">
						<InputField
							label="Nom de la connexion"
							name="name"
							value={formData.name}
							onChange={handleChange}
							loading={loading}
						/>
						<SelectField
							label="Driver"
							selectedKey={formData.driver || ''}
							onValueChange={(val) => setFormData({ ...formData, driver: val })}
							options={[
								{ key: 'pdo_mysql', label: 'MySQL' },
								{ key: 'pdo_sqlsrv', label: 'Microsoft SQL Server' },
							]}
							loading={loading}
						/>
						<InputField
							label="Dernière synchronisation"
							name="lastSync"
							value={
								formData.lastSync
									? new Date(formData.lastSync).toLocaleString()
									: 'Jamais synchronisé'
							}
							onChange={handleChange}
							loading={loading}
							readOnly={true}
						/>
					</CardSection>

					<CardSection icon={faGlobe} title="Détails de la connexion">
						<InputField
							label="Hôte"
							name="host"
							value={formData.host}
							onChange={handleChange}
							loading={loading}
						/>
						<InputField
							label="Port"
							name="port"
							value={formData.port}
							onChange={handleChange}
							loading={loading}
						/>
					</CardSection>

					<CardSection
						icon={faDatabase}
						title="Informations de la base de données"
					>
						<InputField
							label="Base de données"
							name="db"
							value={formData.db}
							onChange={handleChange}
							loading={loading}
						/>
						<InputField
							label="Nom d'utilisateur"
							name="username"
							value={formData.username}
							onChange={handleChange}
							loading={loading}
						/>
						<InputField
							label="Mot de passe"
							name="password"
							type="password"
							value={formData.password}
							onChange={handleChange}
							loading={loading}
						/>
						<InputField
							label="Table principale"
							name="mainTable"
							value={formData.mainTable}
							onChange={handleChange}
							loading={loading}
						/>
					</CardSection>

					<CardSection icon={faDatabase} title="Test de connexion">
						<label className="block mb-2 font-medium text-gray-700">
							Tester la connexion à la base de données :
						</label>
						{/* Boutons de test de connexion */}
						<SecondaryButton
							label="Tester"
							onClick={() => handleTestConnection()}
						/>
						<label className="block mb-2 font-medium text-gray-700">
							Tester l&apos;accès à la table principale de la base de données :
						</label>
						<SecondaryButton
							label="Tester"
							onClick={() => handleTestPreview()}
						/>
						{loadingTest && (
							<p className="mt-2">Test de connexion en cours...</p>
						)}
					</CardSection>
				</div>
			</form>

			<FormSaveActions
				isDirty={isDirty}
				isSubmitting={isSubmitting}
				onCancel={() => navigate('/app/settings/infrastructure')}
				onSave={() => handleSave(false)}
				onSaveAndLeave={() => handleSave(true)}
			/>
		</div>
	);
};

export default ExternalConnectionForm;
